export const createDesiredHead = ({ pageMeta, seo, schemaOrg, metaTags } = {}) => {
  const escapeAttrValue = value => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  const setAttrIfChanged = attr => (el, value) => {
    if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
  };

  const tagFactories = {
    meta: ({ id, attr, key, content }) => ({
      id,
      tagName: 'meta',
      matchSelector: `meta[${attr}="${escapeAttrValue(key)}"]`,
      setStaticAttrs: el => el.setAttribute(attr, key),
      applyValue: setAttrIfChanged('content'),
      value: content ?? '',
    }),
    link: ({ id, rel, href }) => ({
      id,
      tagName: 'link',
      matchSelector: `link[rel="${rel}"]`,
      setStaticAttrs: el => el.setAttribute('rel', rel),
      applyValue: setAttrIfChanged('href'),
      value: href,
    }),
    schemaScript: ({ id, elementId, json }) => ({
      id,
      tagName: 'script',
      matchSelector: `#${elementId}`,
      setStaticAttrs: el => {
        el.type = 'application/ld+json';
        el.id = elementId;
      },
      applyValue: (el, value) => {
        if (el.textContent !== value) el.textContent = value;
      },
      value: json,
    }),
  };

  const tags = new Map();

  const addTag = (type, options) => {
    const tag = tagFactories[type](options);
    tags.set(tag.id, tag);
  };

  if (pageMeta?.description) {
    addTag('meta', {
      id: 'meta:name:description',
      attr: 'name',
      key: 'description',
      content: pageMeta.description,
    });
  }

  if (pageMeta?.isNoIndex || seo?.canonical) {
    addTag('meta', {
      id: 'meta:name:robots',
      attr: 'name',
      key: 'robots',
      content: `${pageMeta?.isNoIndex ? 'noindex' : 'index'},${pageMeta?.follow || 'follow'}`,
    });
  }

  const occurrenceCounts = {};
  (metaTags ?? []).forEach(({ attr, key, content }) => {
    const counterKey = `${attr}:${key}`;
    const occurrence = occurrenceCounts[counterKey] ?? 0;
    occurrenceCounts[counterKey] = occurrence + 1;
    addTag('meta', { id: `meta:${counterKey}:${occurrence}`, attr, key, content });
  });

  if (seo?.canonical) {
    addTag('link', { id: 'link:canonical', rel: 'canonical', href: seo.canonical });
  }

  if (schemaOrg?.['@graph']?.length) {
    addTag('schemaScript', {
      id: 'script:schema-org-page',
      elementId: 'schema-org-page',
      json: JSON.stringify(schemaOrg),
    });
  }

  return { title: pageMeta?.title || null, tags };
};
